import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/fr';
import { Tone } from '@gokrew/models/ui.types';
import {
  NotificationCategory,
  NotificationSubjectType,
  type NotificationActionRequest,
  type NotificationDateGroup,
  type NotificationFeed,
  type NotificationItem,
} from '@gokrew/models/notifications';

dayjs.extend(relativeTime);

/**
 * A portal's own subject-type → route map. The backend deliberately sends no
 * URLs, so each portal owns this: the company portal opens an approval queue
 * where the employee portal opens the employee's own record, and either may
 * have no screen at all for a given resource.
 *
 * `id` is always an id OF `subject.type` — a `TIMESHEET_WEEK` carries a week id,
 * an `EMPLOYEE_TERMINATION` a termination-request id — never a neighbouring
 * resource's. A builder that needs a different resource must read it off
 * `item.context` and return `null` when it is absent, rather than passing `id`
 * somewhere it does not belong.
 */
export type NotificationRouteMap = Partial<
  Record<
    NotificationSubjectType,
    (id: string, item: NotificationItem) => string | null
  >
>;

/**
 * Routes on `subject`, falling back to the `context` resource when a
 * notification carries none. `null` means "render, but don't make it
 * clickable" — which is also what an unknown subject type resolves to, so a
 * newly shipped type degrades instead of breaking.
 */
export const resolveNotificationHref = (
  item: NotificationItem,
  routes: NotificationRouteMap
): string | null => {
  const key = (item.subject?.type ?? item.context?.resourceType) as
    NotificationSubjectType | undefined;
  const id = item.subject?.id ?? item.context?.resourceId;
  const build = key ? routes[key] : undefined;
  return (build && id ? build(id, item) : null) ?? null;
};

const CATEGORY_TONE: Record<NotificationCategory, Tone> = {
  [NotificationCategory.LEAVE_MANAGEMENT]: Tone.WARNING,
  [NotificationCategory.TASKS]: Tone.PRIMARY,
  [NotificationCategory.SURVEYS]: Tone.ACCENT,
  [NotificationCategory.PERFORMANCE]: Tone.SUCCESS,
  [NotificationCategory.ANNOUNCEMENTS]: Tone.SOFT_BLUE,
  [NotificationCategory.TIMESHEETS]: Tone.ACCENT,
  [NotificationCategory.EMPLOYEE_LIFECYCLE]: Tone.SUCCESS,
  [NotificationCategory.COMPENSATION_AND_BENEFITS]: Tone.SUCCESS,
  [NotificationCategory.COMPANY_UPDATES]: Tone.SOFT_BLUE,
  [NotificationCategory.CHANGE_REQUESTS]: Tone.WARNING,
  [NotificationCategory.TRAININGS]: Tone.PRIMARY,
  [NotificationCategory.SYSTEM]: Tone.SECONDARY,
};

/** Unknown categories keep rendering — just without a tone of their own. */
export const getNotificationCategoryTone = (category: NotificationCategory) =>
  CATEGORY_TONE[category] ?? Tone.SECONDARY;

/**
 * The locales side-effect-imported at the top of this file — `en` ships with
 * dayjs. Keep this in sync with `constants/language-options.ts`: dayjs falls
 * back to English for a locale it was never loaded with rather than throwing,
 * so a third language added there without an import here would silently render
 * its dates in English.
 */
const DAYJS_LOCALES = new Set(['en', 'fr']);

const dayjsLocale = (locale: string) => {
  const base = locale.toLowerCase().split('-')[0];
  return DAYJS_LOCALES.has(base) ? base : 'en';
};

/** `12 minutes ago`, in the active locale. */
export const formatNotificationTime = (createdAt: string, locale: string) =>
  dayjs(createdAt).locale(dayjsLocale(locale)).fromNow();

/**
 * Labels a group's `YYYY-MM-DD`. The API returns ISO dates on purpose — only
 * the client knows how to say "Today" in the reader's language.
 *
 * `date` is the reader-local date `mergeNotificationPages` derives, never the
 * company-timezone one the API sends, so it is compared against a local
 * "today" it can actually equal.
 */
export const formatNotificationGroupLabel = (
  date: string,
  locale: string,
  labels: { today: string; yesterday: string }
) => {
  const today = dayjs();
  if (date === today.format('YYYY-MM-DD')) return labels.today;
  if (date === today.subtract(1, 'day').format('YYYY-MM-DD')) {
    return labels.yesterday;
  }
  return dayjs(date).locale(dayjsLocale(locale)).format('D MMMM YYYY');
};

/**
 * Pagination is per item, so the same day can span two pages — pages are
 * bucketed by date rather than concatenated, and items are deduped on `id`
 * because a live push and a refetch can both carry the same row.
 *
 * The bucket date is re-derived from each item's `createdAt` in the reader's
 * timezone instead of reusing `group.date`, which the server resolves in the
 * *company's* timezone. Only the client knows the reader's offset, and a group
 * labelled from one zone but dated in another disagrees with itself whenever
 * the two straddle midnight — an 11 PM notification read from UTC-5 while the
 * company sits in UTC+9 is already filed under tomorrow, so nothing would
 * match "Today" and the newest group would render as a bare date.
 *
 * Items arrive newest-first, so their local dates only ever decrease and the
 * map's insertion order stays descending by date.
 */
export const mergeNotificationPages = (
  pages: NotificationFeed[]
): NotificationDateGroup[] => {
  const byDate = new Map<string, NotificationItem[]>();

  for (const page of pages) {
    for (const group of page.groups) {
      for (const item of group.items) {
        const date = dayjs(item.createdAt).format('YYYY-MM-DD');
        const items = byDate.get(date);
        if (items) items.push(item);
        else byDate.set(date, [item]);
      }
    }
  }

  return [...byDate.entries()].map(([date, items]) => ({
    date,
    items: [...new Map(items.map((item) => [item.id, item])).values()],
  }));
};

/**
 * The request body for an action: whatever the action declared, plus the
 * prompted value under the key the action itself names.
 *
 * The key comes from `prompt.field` and is never hardcoded — the endpoints
 * disagree (`rejectionReason` for a leave rejection, `note` for a timesheet,
 * `adminNote` for a task reopen) and global validation strips unknown keys in
 * silence, so guessing loses the reason without any error.
 */
export const buildNotificationActionBody = (
  request: NotificationActionRequest,
  promptValue?: string
): Record<string, unknown> => ({
  ...request.body,
  ...(request.prompt && promptValue
    ? { [request.prompt.field]: promptValue }
    : {}),
});
